import axios from 'axios';

// In Docker, nginx proxies /api to backend, so we use relative URL
// For local dev without Docker, use the full URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile')
};

// Invoice API
export const invoiceAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  createCorrection: (originalId, data) => api.post(`/invoices/${originalId}/correction`, data),
  getStats: (params) => api.get('/invoices/stats', { params }),
  downloadPDF: async (id) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/invoices/${id}/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `invoice-${id}.pdf`; // fallback filename

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/"/g, ''); // Remove any quotes
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
};

// Dashboard API
export const dashboardAPI = {
  getOverview: (params) => api.get('/dashboard/overview', { params }),
  getTrends: (params) => api.get('/dashboard/trends', { params }),
  getTaxStatistics: (params) => api.get('/dashboard/tax-statistics', { params })
};

// Settings API
export const settingsAPI = {
  getCompanySettings: () => api.get('/settings/company'),
  updateCompanySettings: (data) => api.put('/settings/company', data)
};

// Share API
export const shareAPI = {
  createShare: (data) => api.post('/shares', data),
  getSharesCreated: () => api.get('/shares/created'),
  getSharesReceived: () => api.get('/shares/received'),
  deleteShare: (id) => api.delete(`/shares/${id}`),
  searchUsers: (query) => api.get(`/shares/users/search?q=${query}`)
};

// User Management API (super admin only)
export const userManagementAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  updateRole: (id, role) => api.patch(`/users/${id}/role`, { role }),
  toggleStatus: (id) => api.patch(`/users/${id}/toggle-status`),
  approveUser: (id) => api.patch(`/users/${id}/approve`),
  delete: (id) => api.delete(`/users/${id}`),
  getStats: () => api.get('/users/stats')
};

// Message API
export const messageAPI = {
  getConversations: () => api.get('/messages/conversations'),
  getUserInfo: (userId) => api.get(`/messages/user/${userId}`),
  getMessages: (otherUserId) => api.get(`/messages/${otherUserId}`),
  sendMessage: (data) => api.post('/messages', data),
  markAsRead: (otherUserId) => api.post('/messages/mark-read', { otherUserId }),
  getUnreadCount: () => api.get('/messages/unread/count')
};

// Notification API
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
  getUnreadCount: () => api.get('/notifications/unread/count')
};

// Bank Account API
export const bankAccountAPI = {
  getAll: () => api.get('/bank-accounts'),
  create: (data) => api.post('/bank-accounts', data),
  delete: (id) => api.delete(`/bank-accounts/${id}`),
  setDefault: (id) => api.post(`/bank-accounts/${id}/set-default`)
};

// KSeF API
export const ksefAPI = {
  listInvoices: (params) => api.get('/ksef/invoices', { params }),
  importInvoices: (payload) => api.post('/ksef/invoices/import', payload),
  exportInvoice: (id) => api.post(`/ksef/invoices/${id}/export`)
};

export default api;
